/* eslint-disable max-lines-per-function */
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { toast } from '@/hooks/use-toast';

import { Termin } from '@/domain/termine/shared/types';
import { useDeleteTermin } from '@/domain/termine/hooks/useDeleteTermin';
import { useProfile } from '@/hooks';
import { buildDeleteErrorOutput } from '@/domain/termine/hooks/utils/deleteTerminErrorMessage';

const VERKNUEPFUNG_ERROR_TYPES = [
  'termine_terminDeleteSchichtenVerknuepfungExistError',
  'termine_terminDeleteArbeitszeitenVerknuepfungExistError',
];

type TerminDeleteDialogProps = {
  termin: Termin;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function TerminDeleteDialog({
  termin,
  open,
  onOpenChange,
}: TerminDeleteDialogProps) {
  const { t } = useTranslation();
  const { deleteTermin, loading } = useDeleteTermin();
  const { manager } = useProfile();

  const handleDelete = async () => {
    const deleteResult = await deleteTermin({
      managerId: manager.id,
      terminId: termin.id,
    });

    if (!deleteResult.error) {
      toast({
        title: t('domain.termine.deleteDialog.deleteTermin'),
        description: t('domain.termine.deleteDialog.success.description', {
          name: termin.titel,
        }),
      });
      return;
    }

    const isVerknuepfungsError = VERKNUEPFUNG_ERROR_TYPES.includes(
      deleteResult.errorType ?? '',
    );
    const { toastDescription } = buildDeleteErrorOutput({
      message:
        deleteResult.error?.message ||
        t('domain.termine.deleteDialog.error.description', {
          name: termin.titel,
        }),
      termin,
      isVerknuepfungsError,
      labels: {
        schichtOne: t('entities.shift_one'),
        schichtOther: t('entities.shift_other'),
        azOne: t('entities.workingHour_one'),
        azOther: t('entities.workingHour_other'),
      },
    });

    toast({
      title: t('domain.termine.deleteDialog.deleteTerminFailure'),
      description: toastDescription,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="top-3/12 translate-y-0 p-4">
        <DialogHeader className="*:text-neutrals-900 px-4">
          <DialogTitle className="py-4 text-xl">
            {t('domain.termine.deleteDialog.deleteTermin')}
          </DialogTitle>
          <DialogDescription>
            {t('domain.termine.deleteDialog.deleteTerminDescription')}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-row gap-4 w-full justify-end pt-8">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('domain.termine.deleteDialog.cancel')}
          </Button>
          <Button
            variant="destructive"
            disabled={loading}
            onClick={async () => {
              await handleDelete();
              onOpenChange(false);
            }}>
            {t('domain.termine.deleteDialog.delete')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
